import { OfflineAminoSigner } from '@cosmjs/amino';
import { LedgerSigner } from '@cosmjs/ledger-amino';
import { OfflineSigner } from '@cosmjs/proto-signing';
import { ManagerContext, State } from '@cosmos-kit/core';
import Transport from '@ledgerhq/hw-transport';

import { COSMOS_CHAINS } from 'app/chains/cosmos/cosmos.assets';
import { useNotificationsStore } from 'app/hooks/useNotificationStore';
import { BaseAuthProvider } from 'app/services/Authentication/base/BaseAuthProvider';

import { CosmosChainId } from 'chains/cosmos/cosmos.types';

import {
  WalletProviderInterface,
  CosmosKitAuthProviderOptions,
  CosmosKitWalletType,
} from '../../../types/providers';

const LAST_USED_WALLET_TYPE_KEY = 'last_used_wallet_type';

type ChainWalletClient = {
  client: {
    client: {
      transport: Transport;
    };
    getOfflineSignerAmino: (chainId: string) => Promise<OfflineAminoSigner>;
  };
};

export class CosmosKitAuthProvider
  extends BaseAuthProvider
  implements WalletProviderInterface<CosmosKitAuthProviderOptions>
{
  private _manager: ManagerContext = null!;

  constructor() {
    super('cosmos_kit');
  }

  async connect(
    sessionCheck?: boolean,
    options?: CosmosKitAuthProviderOptions,
  ): Promise<void> {
    this.walletType =
      options?.walletType ||
      (localStorage.getItem(LAST_USED_WALLET_TYPE_KEY) as CosmosKitWalletType);

    if (!options?.manager || !this.walletType) {
      if (sessionCheck) {
        return;
      }

      throw new Error('Missing manager or wallet type');
    }

    this._manager = options.manager;

    //

    const chainWallet = this._getChainWallet(this.elysChain.chainId);

    if (!chainWallet) {
      throw new Error(
        `No wallet available for chain ${this.elysChain.chainId}`,
      );
    }

    try {
      __notify('Connecting wallet...');
      await this._connectChain(this.elysChain.chainId);

      __notify('Getting offline signer...');

      this.offlineSigner = await this.getOfflineSigner(this.elysChain.chainId);

      __notify('Refreshing account...');

      if (!chainWallet.address) {
        throw new Error('No address found');
      }

      this.elysAddress = chainWallet.address;

      //await this.refreshAccount();

      __notify('Account refreshed');

      this.connected = true;

      // save last used wallet type
      localStorage.setItem(LAST_USED_WALLET_TYPE_KEY, this.walletType);
    } catch (error) {
      __notify(`DEBUG: ${error?.toString()}`);

      if (sessionCheck) {
        return;
      }

      console.warn(error);

      // clean up
      this.offlineSigner = null!;

      localStorage.removeItem(LAST_USED_WALLET_TYPE_KEY);
      localStorage.removeItem('cosmos-kit@2:core//current-wallet');

      throw error;
    }
  }

  private _getChainWallet(chainId: CosmosChainId) {
    const mainChainWallet = this._manager.mainWallets.find(
      e => e.walletName === this.walletType,
    );

    return [...(mainChainWallet?.chainWalletMap?.values() ?? [])].find(
      e => e.chainId === chainId,
    );
  }

  async disconnect() {
    try {
      const chainWallet = this._getChainWallet(this.elysChain.chainId);
      chainWallet?.setActions({});

      super.disconnect();

      localStorage.removeItem(LAST_USED_WALLET_TYPE_KEY);
      localStorage.removeItem('cosmos-kit@2:core//current-wallet');

      await chainWallet?.disconnect?.();
    } catch (e) {
      console.warn(e);

      throw new Error('Error disconnecting from CosmosKit.');
    }
  }

  private async _connectChain(chainId: CosmosChainId) {
    const chainRecord = this._manager.chainRecords.find(
      chain => chain.chain?.chain_id === this.elysChain.chainId,
    )!;

    //console.log(chainRecord.chain);

    if (!chainRecord) {
      throw new Error(`ChainRecord not found for ${this.elysChain.chainId}`);
    }

    const chainWallet = this._getChainWallet(chainId);

    if (!chainWallet) {
      throw new Error(`No wallet available for chain ${chainId}`);
    }

    /* try {
      await chainWallet.client.addChain?.(chainRecord);
    } catch (e) {
      console.warn('Chain already exists?', e);
    }

    await chainWallet.client.enable?.(chainId); */

    return new Promise<void>((resolve, reject) => {
      (async () => {
        if (chainWallet.state === State.Done) {
          __notify('Wallet already connected');
          resolve();
          return;
        }

        __notify('Wallet state: ' + chainWallet.state);

        const connectiongTimeout = setTimeout(
          async () => {
            if (chainWallet.state === State.Done) {
              resolve();
              return;
            }

            if (!chainWallet.isActive) {
              chainWallet.activate();
            }

            if (!chainWallet.client) {
              await chainWallet.initClient();
            }

            __notify(`Connecting...`);

            chainWallet.connect();
          },
          chainWallet.state === 'Pending' ? 1500 : 500,
        );

        let checkTimeoutId: ReturnType<typeof setInterval> = null!;

        const handleState = (state: State) => {
          const msg = `state: ${state} ${
            chainWallet.message ||
            (state === State.Error ? chainWallet.rejectMessageSource : '') ||
            ''
          }`;

          __notify(msg);

          switch (state) {
            case State.Init: {
              break;
            }
            case State.Error: {
              clearInterval(checkTimeoutId);
              clearTimeout(connectiongTimeout);
              chainWallet.setActions({});
              reject(new Error(chainWallet.rejectMessageSource));
              break;
            }
            case State.Pending: {
              break;
            }
            case State.Done: {
              clearInterval(checkTimeoutId);
              clearTimeout(connectiongTimeout);
              chainWallet.setActions({});
              resolve();
              break;
            }
          }
        };

        checkTimeoutId = setInterval(() => {
          if (
            chainWallet.state === State.Done ||
            chainWallet.state === State.Error
          ) {
            clearTimeout(checkTimeoutId);
            handleState(chainWallet.state);
          }
        }, 500);

        chainWallet.setActions({
          state: handleState,
        });
      })();
    });
  }

  async resolveAddress(chainId: CosmosChainId): Promise<string> {
    const signer = await this.getOfflineSigner(chainId);

    return (await signer.getAccounts())[0].address;
  }

  async getOfflineSigner(chainId: CosmosChainId) {
    if (this.elysChain.chainId === chainId && this.offlineSigner) {
      return this.offlineSigner;
    }

    await this._connectChain(chainId);

    const chainWallet = this._getChainWallet(chainId);

    if (chainWallet!.walletName === 'ledger-web-usb-hid') {
      const chain = COSMOS_CHAINS[chainId];
      return new LedgerSigner((chainWallet as unknown as ChainWalletClient).client.client.transport, {
        prefix: chain.addressPrefix,
      });
    }

    /**
     * This call works for both Amino based transactions (Ledger connected on Leap, Keplr, etc)
     * and transactions created from direct compatible signers.
     */
    return (chainWallet as unknown as ChainWalletClient)!.client!.getOfflineSignerAmino(chainId);
  }

  async createSigningStargateClient(
    chainId: string,
    offlineSigner?: OfflineSigner,
  ) {
    offlineSigner = offlineSigner ?? (await this.getOfflineSigner(chainId as CosmosChainId));

    const client = await super.createSigningStargateClient(
      chainId as CosmosChainId,
      offlineSigner,
    );

    return client;
  }

  async getOfflineSignerAmino(chainId: CosmosChainId) {
    const chainWallet = this._getChainWallet(chainId);
    return chainWallet!.client!.getOfflineSignerAmino!(
      chainId,
    ) as OfflineAminoSigner;
  }
}

// TODO: Debug function
export function __notify(msg: string) {
  console.log(msg);

  return;

  useNotificationsStore.getState().addNotification({
    id: `wallet-${Math.random()}`,
    content: msg,
    variant: 'success',
    duration: 6000,
  });
}
